// THE ONLY FILE THAT IMPORTS SUPABASE
// No other file may import @supabase/supabase-js or call createClient

import { createClient } from '@supabase/supabase-js';
import * as _logger from './logger.js'; // eslint-disable-line no-unused-vars
import { TABLES } from './database-tables.js';

let _client = null;

function getClient() {
  if (_client) return _client;

  const config = window.APP_CONFIG?.supabase;
  if (!config?.url || !config?.anonKey) {
    throw new Error('Supabase config missing from APP_CONFIG');
  }

  // Keep auth config minimal — Safari/iPad ITP compatibility
  // flowType: 'implicit' avoids PKCE code-exchange issues when 301 redirects
  // (non-www → www) interfere with the auth callback URL
  _client = createClient(config.url, config.anonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'implicit',
    },
  });
  return _client;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function signIn(email, password) {
  const { data, error } = await getClient().auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function resetPassword(email) {
  const { error } = await getClient().auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/admin/set-password`,
  });
  if (error) throw error;
}

export async function updatePassword(newPassword) {
  const { error } = await getClient().auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function signOut() {
  const { error } = await getClient().auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  const { data, error } = await getClient().auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function getCurrentUser() {
  const { data, error } = await getClient().auth.getUser();
  if (error) return null;
  return data.user;
}

export function onAuthStateChange(callback) {
  return getClient().auth.onAuthStateChange((event, session) => {
    callback(session?.user ?? null, event);
  });
}

// ── Bootstrap / Super Admin ───────────────────────────────────────────────────

export async function checkIsSuperAdmin() {
  const { data, error } = await getClient().rpc('is_super_admin');
  if (error) throw error;
  return data;
}

// ── Wineries ──────────────────────────────────────────────────────────────────

export async function getWineryBySlug(slug) {
  const { data, error } = await getClient()
    .from(TABLES.WINERIES)
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .single();
  if (error) throw error;
  return data;
}

export async function getAllWineries() {
  const { data, error } = await getClient()
    .from(TABLES.WINERIES)
    .select('*')
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return data;
}

export async function createWinery(wineryData) {
  const { data, error } = await getClient()
    .from(TABLES.WINERIES)
    .insert(wineryData)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateWinery(id, wineryData) {
  const { data, error } = await getClient()
    .from(TABLES.WINERIES)
    .update(wineryData)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function toggleWineryActive(id, isActive) {
  return updateWinery(id, { is_active: isActive });
}

// ── Wines ─────────────────────────────────────────────────────────────────────

export async function getWineById(id) {
  const { data, error } = await getClient()
    .from(TABLES.WINES)
    .select('*, translations, wineries(name, slug, theme_preference)')
    .eq('id', id)
    .eq('is_active', true)
    .single();
  if (error) throw error;
  return data;
}

export async function getWinesByWinery(wineryId) {
  const { data, error } = await getClient()
    .from(TABLES.WINES)
    .select('*')
    .eq('winery_id', wineryId)
    .order('name');
  if (error) throw error;
  return data;
}

export async function createWine(wineData) {
  const { data, error } = await getClient()
    .from(TABLES.WINES)
    .insert(wineData)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateWine(id, wineData) {
  const { data, error } = await getClient()
    .from(TABLES.WINES)
    .update(wineData)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function toggleWineActive(id, isActive) {
  return updateWine(id, { is_active: isActive });
}

// ── Flights ──────────────────────────────────────────────────────────────────

/**
 * Public fetch: active flight with its active wines and winery info.
 */
export async function getPublicFlightById(id) {
  const { data, error } = await getClient()
    .from(TABLES.FLIGHTS)
    .select('*, translations, wineries(name, slug, theme_preference), flight_wines(sort_order, wines(id, name, varietal, vintage_year, price, description, tasting_notes, food_pairings, image_url, is_active, translations))')
    .eq('id', id)
    .eq('is_active', true)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Public fetch: active wines for a winery, plus active flights.
 */
export async function getPublicWineryData(slug) {
  const winery = await getWineryBySlug(slug);

  const [{ data: wines, error: wErr }, { data: flights, error: fErr }] = await Promise.all([
    getClient()
      .from(TABLES.WINES)
      .select('id, name, varietal, vintage_year, price, image_url, translations')
      .eq('winery_id', winery.id)
      .eq('is_active', true)
      .order('name'),
    getClient()
      .from(TABLES.FLIGHTS)
      .select('id, name, description, translations, flight_wines(wine_id)')
      .eq('winery_id', winery.id)
      .eq('is_active', true)
      .order('sort_order'),
  ]);

  if (wErr) throw wErr;
  if (fErr) throw fErr;

  return { winery, wines, flights };
}

export async function getFlightsByWinery(wineryId) {
  const { data, error } = await getClient()
    .from(TABLES.FLIGHTS)
    .select('*, flight_wines(wine_id)')
    .eq('winery_id', wineryId)
    .order('sort_order');
  if (error) throw error;
  return data;
}

export async function getFlightById(id) {
  const { data, error } = await getClient()
    .from(TABLES.FLIGHTS)
    .select('*, flight_wines(id, wine_id, sort_order, wines(id, name, varietal, is_active))')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

export async function createFlight(flightData) {
  const { data, error } = await getClient()
    .from(TABLES.FLIGHTS)
    .insert(flightData)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateFlight(id, flightData) {
  const { data, error } = await getClient()
    .from(TABLES.FLIGHTS)
    .update(flightData)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function toggleFlightActive(id, isActive) {
  return updateFlight(id, { is_active: isActive });
}

export async function setFlightWines(flightId, wineIds) {
  // Remove existing wines for this flight
  const { error: delErr } = await getClient()
    .from(TABLES.FLIGHT_WINES)
    .delete()
    .eq('flight_id', flightId);
  if (delErr) throw delErr;

  if (wineIds.length === 0) return [];

  // Insert new set with sort_order
  const rows = wineIds.map((wineId, i) => ({
    flight_id: flightId,
    wine_id: wineId,
    sort_order: i,
  }));
  const { data, error } = await getClient()
    .from(TABLES.FLIGHT_WINES)
    .insert(rows)
    .select();
  if (error) throw error;
  return data;
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export async function getAdminWineries(userId) {
  const { data, error } = await getClient()
    .from(TABLES.WINERY_ADMINS)
    .select('*, wineries(*)')
    .eq('user_id', userId);
  if (error) throw error;
  return data || [];
}

/**
 * Returns array of { role, winery_id } for the given user.
 */
export async function getUserRoles(userId) {
  const { data, error } = await getClient()
    .from(TABLES.WINERY_ADMINS)
    .select('role, winery_id')
    .eq('user_id', userId);
  if (error) throw error;
  return data || [];
}

/**
 * All wineries (including inactive) for super admin management.
 */
export async function getAllWineriesAdmin() {
  const { data, error } = await getClient()
    .from(TABLES.WINERIES)
    .select('*')
    .order('name');
  if (error) throw error;
  return data;
}

// ── Staff ─────────────────────────────────────────────────────────────────────

/**
 * Get staff list for a winery via the get_winery_staff DB function.
 * Returns [{ admin_id, user_id, email, role, created_at }].
 */
export async function getWineryStaff(wineryId) {
  const { data, error } = await getClient()
    .rpc('get_winery_staff', { target_winery_id: wineryId });
  if (error) throw error;
  return data;
}

/**
 * Invite a user to a winery via the invite-user Edge Function.
 */
export async function inviteUser(email, wineryId, role) {
  const { data, error } = await getClient()
    .functions.invoke('invite-user', {
      body: {
        email,
        winery_id: wineryId,
        role,
        redirect_to: `${window.location.origin}/admin/set-password`,
      },
    });
  if (error) {
    let detail = error.message || 'Unknown edge function error';
    if (error.context && typeof error.context.json === 'function') {
      try {
        const body = await error.context.json();
        if (body?.error) detail = body.error;
      } catch (_) { /* ignore parse errors */ }
    }
    throw new Error(detail);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Update a winery admin's role (e.g. promote staff → owner).
 * @param {string} adminId — winery_admins row ID
 * @param {string} newRole — 'owner' or 'staff'
 */
export async function updateWineryAdminRole(adminId, newRole) {
  const { error } = await getClient()
    .rpc('update_winery_admin_role', { target_admin_id: adminId, new_role: newRole });
  if (error) throw error;
}

/**
 * Remove a winery admin by their winery_admins row ID.
 * Uses a security definer RPC for reliable permission checks.
 */
export async function removeWineryAdmin(adminId) {
  const { error } = await getClient()
    .rpc('remove_winery_admin', { target_admin_id: adminId });
  if (error) throw error;
}

// ── Storage ──────────────────────────────────────────────────────────────────

/**
 * Upload an image to Supabase Storage.
 * @param {string} bucket — storage bucket name (e.g. 'wine-images', 'winery-logos')
 * @param {string} path — file path within the bucket (e.g. 'w1/photo.jpg')
 * @param {File} file — the File object to upload
 * @returns {string} — the public URL of the uploaded file
 */
export async function uploadImage(bucket, path, file) {
  const { error } = await getClient()
    .storage.from(bucket)
    .upload(path, file, { upsert: true });
  if (error) throw error;
  return getPublicUrl(bucket, path);
}

/**
 * Get the public URL for a file in Supabase Storage.
 */
export function getPublicUrl(bucket, path) {
  const { data } = getClient()
    .storage.from(bucket)
    .getPublicUrl(path);
  return data.publicUrl;
}

// ── Translations ────────────────────────────────────────────────────────────

/**
 * Call the translate-content Edge Function to translate text via MyMemory.
 * @param {Object} fields — key/value pairs to translate (e.g. { name: 'Red Wine', description: '...' })
 * @param {string} targetLocale — target locale code (e.g. 'es')
 * @returns {Object} — translated key/value pairs
 */
export async function translateContent(fields, targetLocale) {
  const { data, error } = await getClient()
    .functions.invoke('translate-content', {
      body: { fields, target_locale: targetLocale },
    });
  if (error) {
    // Extract details from FunctionsHttpError / FunctionsRelayError
    let detail = error.message || 'Unknown edge function error';
    if (error.context && typeof error.context.json === 'function') {
      try {
        const body = await error.context.json();
        if (body?.error) detail = body.error;
      } catch (_) { /* ignore parse errors */ }
    }
    throw new Error(detail);
  }
  if (data?.error) throw new Error(data.error);
  return data.translations;
}
